import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Button, Icon } from 'antd';
import { FaFileDownload } from 'react-icons/fa';
import useRequestActAvailable from '../queries/useRequestActAvailable';
import request from '../helpers/request';
import AvailableDocumentationModal from './AvailableDocumentationModal/AvailableDocumentationModal';
import useChangeModalVisible from '../hooks/useChangeModalVisible';
import { getBlobUrl } from '../helpers/getBlobUrl';
import { exportActions } from '../actions/export.actions';

const chunkSize = 200;
const exportToExcelId = 'exportToExcel';

const fetchAct = async ({ act, requestIds }) => {
  const { url, name, ...otherActData } = act;

  const data = await request.get(
    url,
    { requestIds },
    `Не удалось загрузить ${name}_${requestIds[0]}-${
      requestIds[requestIds.length - 1]
    }`,
    undefined,
    undefined,
    'comma'
  );

  return {
    encodedFile: data,
    name: `${name}_${requestIds[0]}-${requestIds[requestIds.length - 1]}`,
    ...otherActData,
  };
};

const fetchAndPrintActs = async ({
  setLoading,
  hideModal = () => {},
  setLinks,
  selectedRequestIds,
  selectedActs,
  exportToExcelFn,
}) => {
  setLoading(true);

  let exportToExcelAct = null;

  const acts = selectedActs.reduce((acc, act) => {
    if (act.id === exportToExcelId) {
      exportToExcelAct = act;
    } else {
      acc.push(act);
    }

    return acc;
  }, []);

  const chunks = Array.from(
    {
      length: Math.ceil(selectedRequestIds.length / chunkSize),
    },
    (_, i) => selectedRequestIds.slice(i * chunkSize, i * chunkSize + chunkSize)
  );

  const results = await Promise.allSettled(
    chunks.flatMap((chunk) =>
      acts.map((act) => fetchAct({ act, requestIds: chunk }))
    )
  );

  setLoading(false);
  hideModal();

  results.forEach((result) => {
    const { encodedFile, extension, name } = result.value;
    if (encodedFile) {
      const url = getBlobUrl(encodedFile, extension);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name}.${extension}`);
      document.body.appendChild(link);
      link.click();

      setLinks((prevLinks) => [...prevLinks, link]);
    }
  });

  if (exportToExcelAct) {
    exportToExcelFn(selectedRequestIds);
  }
};

const PrintBtn = ({ loading, onClick }) => {
  return (
    <Tooltip title="Сформировать документы" placement="bottom">
      <Button
        type="primary"
        shape="circle"
        icon={loading ? 'loading' : ''}
        style={{ marginRight: '10px', fontSize: '16px' }}
        onClick={onClick}
      >
        {!loading && <Icon component={FaFileDownload} />}
      </Button>
    </Tooltip>
  );
};

const Printer = ({ selectedRequestIds, availableActs }) => {
  const dispatch = useDispatch();

  const [printActsLoading, setPrintActsLoading] = useState(false);
  const [links, setLinks] = useState([]);

  return (
    <PrintBtn
      onClick={async () => {
        await fetchAndPrintActs({
          setLoading: setPrintActsLoading,
          setLinks,
          selectedRequestIds,
          selectedActs: availableActs,
          exportToExcelFn: (selectedIds) =>
            dispatch(exportActions.exportRequests(selectedIds)),
        });

        if (links.length) {
          links.forEach((link) => {
            document.body.removeChild(link);
          });

          setLinks([]);
        }
      }}
      loading={printActsLoading}
    />
  );
};

const PrinterWithModal = ({ selectedRequestIds, availableActs }) => {
  const { visible, showModal, hideModal } = useChangeModalVisible();

  const dispatch = useDispatch();

  const [selectedActIds, setSelectedActIds] = useState([]);
  const [selectedActs, setSelectedActs] = useState([]);
  const [printActsLoading, setPrintActsLoading] = useState(false);
  const [links, setLinks] = useState([]);

  const handleAfterClose = () => {
    setSelectedActIds([]);
    setSelectedActs([]);

    if (links.length) {
      links.forEach((link) => {
        document.body.removeChild(link);
      });

      setLinks([]);
    }
  };

  const handleSelectChange = (value, options) => {
    setSelectedActIds(value);
    setSelectedActs(options.map((option) => option.props.item));
  };

  return (
    <>
      <PrintBtn onClick={showModal} />
      <AvailableDocumentationModal
        visible={visible}
        okBtnText="Сформировать"
        modalTitle="Формирование документов"
        onCancel={hideModal}
        onOk={() =>
          fetchAndPrintActs({
            setLoading: setPrintActsLoading,
            hideModal,
            setLinks,
            selectedRequestIds,
            selectedActs,
            exportToExcelFn: (selectedIds) =>
              dispatch(exportActions.exportRequests(selectedIds)),
          })
        }
        selectTitle="Выберите документы"
        selectPlaceholder="Выберите один или несколько документов"
        selectedItems={selectedActIds}
        onSelectChange={handleSelectChange}
        items={availableActs}
        modalProps={{
          afterClose: handleAfterClose,
          destroyOnClose: true,
          confirmLoading: printActsLoading,
        }}
      />
    </>
  );
};

const defaultActs = [{ id: exportToExcelId, name: 'Выгрузить в Excel' }];

const DspxPrinterComponent = () => {
  const [renderableActs, setRenderableActs] = useState(defaultActs);
  const selectedRequestIds = useSelector(
    (state) => state.requestListStore.selectedRequests
  );

  const { data: availableActs, isFetching: isAvailableActsFetching } =
    useRequestActAvailable({
      staletime: Infinity,
    });

  const props = useMemo(
    () => ({
      selectedRequestIds,
      availableActs: renderableActs,
    }),
    [renderableActs, selectedRequestIds]
  );

  useEffect(() => {
    let newRenderableActs = defaultActs;

    if (availableActs?.length) {
      newRenderableActs = defaultActs.concat(availableActs);
    }

    setRenderableActs(newRenderableActs);
  }, [availableActs]);

  switch (true) {
    case !selectedRequestIds.length:
      return null;
    case isAvailableActsFetching:
      return <PrintBtn loading />;
    case renderableActs.length === 1:
      return <Printer {...props} />;
    case renderableActs.length > 1:
      return <PrinterWithModal {...props} />;
    default:
      return null;
  }
};

export default DspxPrinterComponent;
